import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ImpersonatedShipper, ShipperSearch } from '@types';
import { ActiveShipper, JWT, ShipperSettings } from '@state';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useGetShipperSettings } from '@api';
import {
	IMPERSONATION_LOCAL_STORAGE_KEY,
	buildImpersonatedShipper,
	canImpersonateFromJwt,
	fetchShipperById,
	getIdOrExternalId,
} from './useImpersonation.helpers';

export const useImpersonateShipper = () => {
	const [search, setSearch] = useSearchParams();
	const { hash } = useLocation();
	const navigate = useNavigate();
	const jwt = useRecoilValue(JWT);
	const sid = search.get('sid');
	const storedSid = sessionStorage.getItem(IMPERSONATION_LOCAL_STORAGE_KEY.shipper);
	const setActiveShipper = useSetRecoilState(ActiveShipper);
	const setShipperSettings = useSetRecoilState(ShipperSettings);
	const { callGetShipperSettings } = useGetShipperSettings();
	const shipperIdOrExternalId = getIdOrExternalId(sid);

	// Keep the ShipperSettings atom in sync with the impersonated shipper so
	// settings-driven UI (e.g. bulk upload visibility) reflects that shipper.
	const fetchShipperSettings = (shipperId?: number | string) => {
		if (shipperId === undefined || shipperId === null || shipperId === '') return;
		callGetShipperSettings({
			params: { shipper_id: `${shipperId}` },
			headers: { 'X-Shipper-Token': `${shipperId}` }
		}).then((res) => {
			if (res) setShipperSettings(res.data);
		}).catch((err) => {
			if (err && err.cause) console.error(err);
		});
	}

	/* Unlike setSearch, keeps the fragment. A link opened in a new tab may name a row there, such as
	   an Item type's in the rate card settings, and this runs before the page draws it. */
	const setSid = (shipperId: ImpersonatedShipper['id']) => {
		const params = new URLSearchParams(search);
		params.set('sid', `${shipperId}`);
		navigate({ search: `?${params}`, hash });
	}

	// Auto-resolve the active shipper from the ?sid param / sessionStorage (Router init path).
	const resolveShipperFromRoute = async () => {
		if (!canImpersonateFromJwt(jwt)) return;
		if (shipperIdOrExternalId !== null) {
			if (storedSid !== null) {
				const activeShipper: ImpersonatedShipper = JSON.parse(storedSid);
				if (activeShipper.id !== shipperIdOrExternalId && activeShipper.customer_id !== shipperIdOrExternalId) {
					try {
						const shipper = await fetchShipperById(shipperIdOrExternalId);
						sessionStorage.setItem(IMPERSONATION_LOCAL_STORAGE_KEY.shipper,
							JSON.stringify(shipper));
						setSid(shipper.id);
						setActiveShipper(shipper);
						fetchShipperSettings(shipper.id);
					} catch (error) {
						console.error('Failed to fetch shipper:', error);
						throw error;
					}
				} else {
					setSid(activeShipper.id);
					setActiveShipper(activeShipper);
					fetchShipperSettings(activeShipper.id);
				}
			} else {
				try {
					const fetchedShipper = await fetchShipperById(shipperIdOrExternalId);
					const shipper: ImpersonatedShipper = {
						...fetchedShipper,
						external_id: fetchedShipper.customer_id
					};
					sessionStorage.setItem(IMPERSONATION_LOCAL_STORAGE_KEY.shipper,
						JSON.stringify(shipper));
					setSid(shipper.id);
					setActiveShipper(shipper);
					fetchShipperSettings(shipper.id);
				} catch (error) {
					console.error('Failed to fetch shipper:', error);
				}
			}
		} else if (storedSid !== null) {
			const activeShipper: ImpersonatedShipper = JSON.parse(storedSid);
			setSid(activeShipper.id);
			setActiveShipper(activeShipper);
			fetchShipperSettings(activeShipper.id);
		}
	}

	// Explicitly impersonate a shipper from a search result or an id.
	const impersonateShipper = async (impersonation: ShipperSearch | number | string) => {
		if (!canImpersonateFromJwt(jwt)) return;
		if (typeof impersonation === 'number' || typeof impersonation === 'string') {
			const shipper = await fetchShipperById(impersonation);
			setActiveShipper(shipper);
			fetchShipperSettings(shipper.id);
			sessionStorage.setItem(IMPERSONATION_LOCAL_STORAGE_KEY.shipper, JSON.stringify(shipper));
			setSearch((params) => {
				params.set('sid', `${shipper.id}`)
				return params
			});
		} else {
			const shipper = buildImpersonatedShipper(impersonation);
			setActiveShipper(shipper);
			fetchShipperSettings(shipper.id);
			sessionStorage.setItem(IMPERSONATION_LOCAL_STORAGE_KEY.shipper, JSON.stringify(shipper));
			setSearch((params) => {
				params.set('sid', `${shipper.id}`)
				return params
			});
		}
	}

	// Clear the active shipper without navigating (used by the no-arg refrain()).
	const clearShipper = () => {
		sessionStorage.removeItem(IMPERSONATION_LOCAL_STORAGE_KEY.shipper);
		setActiveShipper(null);
		// Only clear impersonation-sourced settings; real shipper logins keep theirs.
		if (canImpersonateFromJwt(jwt)) setShipperSettings(null);
	}

	// Stop impersonating the shipper and return to the shipper dashboard.
	const refrainShipper = () => {
		clearShipper();
		if (search.has('sid')) {
			search.delete('sid');
			setSearch(search);
		}
		navigate('/shipper/dashboard');
	}

	return {
		resolveShipperFromRoute,
		impersonateShipper,
		clearShipper,
		refrainShipper,
	}
}
